import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Typography from '../Typography';
import Button from '../Button';
import Option from '../OptionDisplay/Option';
import PreviewImage from '../PreviewImage';
import styles from './OptionDisplay.module.sass';
import { useHistory, useParams } from 'react-router';
import { CustomerContext } from '../../contexts/CustomerContext';
import { pluralize } from '../../utils/functions'
import useQueryString from '../../hooks/use-query-string';

const OptionDisplay = ({ backgroundUrl, multiPoseHandler, studentImageUrl, option, price, imageTypes, currentOrderPackage, packageId, accessCodeOnly }) => {
  const { package_id } = useParams();
  const history = useHistory();
  const { customer, setCustomer } = useContext(CustomerContext);
  const { preorder } = useQueryString()

  if (!(option && price)) return null;

  const isDigital = imageType => imageType.name.toLowerCase().includes('digital');
  const isClassPhoto = imageType => imageType.name.toLowerCase().includes('class');
  const filter = imageType => !(isClassPhoto(imageType) || isDigital(imageType));

  const handleContinue = async () => {
    currentOrderPackage.apiOptionId = option.id;
    currentOrderPackage.apiOption = option;
    currentOrderPackage.packageId = packageId;
    currentOrderPackage.studentId = customer.currentStudent.id

    let success = await currentOrderPackage.save({ with: ['apiOption.id', 'background.id'] })

    if (success) {
      setCustomer({ ...customer, currentOrderPackage });
      history.push(`/addons/${package_id}`);
    } else {
      toast('There was an error adding this item to your cart. Please refresh and try again.', { type: 'error' })
    }
  };

  const hasCustomImage = option.imageUrl && !option.imageUrl.includes('missing.png')

  return (
    <div className={cn(styles.container, option.bestValue && styles.bestValueContainer)}>
      {(studentImageUrl && backgroundUrl) && (
        <div className={styles.previewContainer}>
          <Option
            backgroundUrl={backgroundUrl}
            foregroundUrl={hasCustomImage ? option.imageUrl : studentImageUrl}
            hasCustomImage={hasCustomImage}
            option={option}
            accessCodeOnly={accessCodeOnly}
          />
          {multiPoseHandler && !preorder && (
            <Typography fontStyle="italic" style={{ color: option.bestValue ? '#fff' : 'inherit' }}>
              SELECT UP TO
              {' '}
              {pluralize('POSE', option.poses || 1, 'S')}!
            </Typography>
          )}
        </div>
      )}
      {!backgroundUrl && studentImageUrl.endsWith('jpg') && (
        <div className={styles.previewContainer}>
          <PreviewImage foregroundUrl={studentImageUrl} backgroundUrl={studentImageUrl} />
        </div>
      )}
      <div className={styles.infoContainer}>
        <div className={styles.header}>
          <div className={styles.headerInnerWrapper}>
            <div>
              <Typography tag='span' weight={600} classes={styles.packageTitle}>
                {`Package ${option.abbreviation.toUpperCase()}`}
              </Typography>
              <Typography tag='span' weight={600} classes={styles.price}>
                {price.price ? `$${price.price.toString()}` : '$0'}
              </Typography>
            </div>
            {option.bestValue && (
              <Typography tag='span' weight={600} classes={styles.tempBestValue}>Best Value!</Typography>
            )}
          </div>
          {option.name.length > 1 && (
            <Typography tag='div' weight={600} classes={styles.packageTitle}>
              {option.name}
            </Typography>
          )}
        </div>
        <div className={styles.includedItemsList}>
          {imageTypes
            .filter(imageType => filter(imageType))
            .sort((a, b) => a.id - b.id)
            .map(imageType => (
              <Typography
                tag='span'
                weight={500}
                key={imageType.id}
                classes={cn(styles.paddingRight, styles.typography)}
              >
                {imageType.name}
              </Typography>
          ))}
          {imageTypes.some(imageType => isClassPhoto(imageType)) && (
            <Typography tag='span' weight={500} classes={cn(styles.paddingRight, styles.typography)}>(1) Class Picture</Typography>
          )}
          {imageTypes.some(imageType => isDigital(imageType)) && (
            <Typography tag='span' weight={600} classes={cn(styles.paddingRight, styles.typography)}>
              {option.bestValue ? 'PLUS DIGITAL FILE!': 'Digital File'}
            </Typography>
          )}
        </div>
        <Button
          fullWidth
          variant='primary'
          classes={cn(styles.selectBtn, option.bestValue && styles.bestValueBtn)}
          onClick={multiPoseHandler ? () => multiPoseHandler(option.id) : handleContinue}
        >
          Select
        </Button>
      </div>
    </div>
  );
};

OptionDisplay.defaultProps = {
  imageTypes: [],
  multiPose: null,
};

OptionDisplay.propTypes = {
  backgroundUrl: PropTypes.string,
  studentImageUrl: PropTypes.string,
  multiPose: PropTypes.func,
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    abbreviation: PropTypes.string,
    poses: PropTypes.number,
    slug: PropTypes.string,
    name: PropTypes.string.isRequired,
    bestValue: PropTypes.bool.isRequired,
    additionalPoseCharge: PropTypes.number,
    download: PropTypes.bool.isRequired,
    emailPicture: PropTypes.bool.isRequired,
    superValue: PropTypes.bool.isRequired,
    without: PropTypes.bool.isRequired,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
  }),
  imageTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  price: PropTypes.shape({
    id: PropTypes.string.isRequired,
    begin: PropTypes.string,
    enddate: PropTypes.string,
    price: PropTypes.number.isRequired,
    createdAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
  })
};

export default OptionDisplay;
